import { Colors } from '@app/config/colors';
import { APICliqueiAchei } from '@app/services/cliqueiachei-api';
import { CliqueiAcheiData, Menu, MenuItem, MenuTypes, Restaurant } from '@app/types';
import { adaptCliqueiAcheiClientResponseDataTo } from '@app/utils/adapters';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fakeMenus } from '@app/__tests__/_data';
import { useRouter } from 'next/router';
import { deepCloneObject } from '@app/utils/object';
import { MenuItemSessionData } from '@app/components/MenuItemDetailsModal/storage';
import { LocalStorage } from '@app/utils/storage';
import { FavoritedMenuItemsStorage } from '@app/services/storage';
import { MenuItemFactory } from '@app/utils/menu';
import {
  MenuItemFavorited,
  MenuItemFavoritedOfBag,
  MenuItemTypeGuard,
  MenuItemWithDetails,
  MenuItemWithDetailsOfBag,
} from '@app/types/menu-item';

export function getRestaurantColors(cliqueiAcheiData: CliqueiAcheiData) {
  const primaryColor = cliqueiAcheiData.CorMenu || Colors.DefaultPrimaryColor;
  const secondaryColor = cliqueiAcheiData.CorMenuText || Colors.DefaultTintColor;
  return { primaryColor, secondaryColor };
}

const TEN_MINUTES_IN_MILLISECONDS = 10 * 60 * 1000;

export async function useAutoCliqueiAcheiDataUpdate(
  cliqueiAcheiData: CliqueiAcheiData | undefined,
  onFetched: (newData: CliqueiAcheiData) => void
) {
  const lastTimeCliqueiAcheiDataHasFetched = useRef<number | null>(null);
  useEffect(() => {
    async function handleVisibilityChange() {
      if (
        !cliqueiAcheiData ||
        lastTimeCliqueiAcheiDataHasFetched.current === null ||
        document.visibilityState === 'hidden'
      )
        return;

      const timeSinceLastTimeDataHasFetched = Date.now() - lastTimeCliqueiAcheiDataHasFetched.current;
      if (timeSinceLastTimeDataHasFetched < TEN_MINUTES_IN_MILLISECONDS) return;

      console.log('ATUALIZANDO DADOS...');
      const { data: cliqueiAcheiClient } = await APICliqueiAchei.fetchClientDataById(cliqueiAcheiData.Cod_Cliente);
      const cliqueiAcheiPedidosClient = adaptCliqueiAcheiClientResponseDataTo(cliqueiAcheiClient);
      onFetched(cliqueiAcheiPedidosClient);

      lastTimeCliqueiAcheiDataHasFetched.current = Date.now();
    }

    if (lastTimeCliqueiAcheiDataHasFetched.current === null) {
      lastTimeCliqueiAcheiDataHasFetched.current = Date.now();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [onFetched]);
}

/**
 * This hook is just for help in testing
 */
export function useRestaurantMockedData(
  restaurant: Restaurant | undefined,
  isMenusFiltered: boolean,
  onDataMocked: (mockedData: Restaurant) => void
) {
  const { query } = useRouter();

  useEffect(() => {
    console.log('isMenusFiltered: ', isMenusFiltered);
    if (isNotElegibleToMock()) return;

    console.warn('Using menus data mocked!');
    // @ts-ignore
    onDataMocked({ ...(restaurant as Restaurant), menus: deepCloneObject(fakeMenus), hasMockedData: true });

    function isNotElegibleToMock() {
      return (
        process.env.NODE_ENV === 'production' ||
        isMenusFiltered ||
        query.use_mock_menus_data !== 'true' ||
        !restaurant ||
        // @ts-ignore
        restaurant.hasMockedData
      );
    }
  }, [restaurant, query, isMenusFiltered]);
}

export function useOnlyRegisterOrderOption(clientId: number | undefined): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [onlyRegisterOrder, setOnlyRegisterOrder] = useState(false);

  const alreadyLoadFromStorage = useRef(false);
  useEffect(() => {
    if (!clientId) {
      setOnlyRegisterOrder(false);
      return;
    }

    const value = OnlyRegisterOrderOptionStorage.get(clientId);
    setOnlyRegisterOrder(Boolean(value));
    alreadyLoadFromStorage.current = true;
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !alreadyLoadFromStorage.current) return;
    OnlyRegisterOrderOptionStorage.save(clientId, onlyRegisterOrder);
  }, [clientId, onlyRegisterOrder]);

  return [onlyRegisterOrder, setOnlyRegisterOrder];
}

const OnlyRegisterOrderOptionStorage = {
  save(id: string | number, value: boolean) {
    LocalStorage.setItem(`only-register-order-${id}`, String(value));
  },
  get(id: string | number) {
    const value = LocalStorage.getItem(`only-register-order-${id}`);
    return value ? value === 'true' : null;
  },
};

export function injectMenuItemSessionData(
  menuItem: MenuItemWithDetails | MenuItemWithDetailsOfBag | MenuItemFavorited | MenuItemFavoritedOfBag,
  previous: MenuItemSessionData
) {
  console.log('injecting: ', previous);
  if (MenuItemTypeGuard.isWithDetailsOfBag(menuItem)) {
    menuItem.amount = previous.amount;
  }

  const groupsToInject = Object.keys(previous.complements);
  for (const group of menuItem.gruposDeComplementos) {
    if (!groupsToInject.includes(group.id)) continue;

    for (const complement of group.complementos) {
      if (previous.complements[group.id][complement.id])
        complement.amount = previous.complements[group.id][complement.id];
    }
  }

  return menuItem;
}

export function useFetchCliqueiAcheiData(
  cliqueiAcheiId: number | undefined,
  shouldIgnore: boolean,
  onFetched: (newDate: CliqueiAcheiData) => void
) {
  useEffect(() => {
    async function updateData() {
      if (!cliqueiAcheiId || shouldIgnore) return;
      try {
        const dataUpdated = await APICliqueiAchei.fetchClientDataAndAdaptToMenu(cliqueiAcheiId);
        onFetched(dataUpdated);
      } catch (error: any) {
        console.warn('Error on update data: ' + error.message);
      }
    }
    updateData();
  }, []);
}

type MenusOptions = {
  isAInternalMenu: boolean;
  isOrderedOnRestaurant: boolean;
  isLoggedInClientArea: boolean;
};

export function useRestaurantMenus(originalMenus: Menu[], options: MenusOptions) {
  const { isAInternalMenu, isOrderedOnRestaurant, isLoggedInClientArea } = options;

  const menus = useMemo(() => {
    if (isLoggedInClientArea) return [...originalMenus];
    if (!isAInternalMenu && isOrderedOnRestaurant) return originalMenus.filter(filterAvailableInLocalOrder);
    if (!isAInternalMenu && !isOrderedOnRestaurant) return originalMenus.filter(filterAvailableToDelivery);
    return [...originalMenus];

    function filterAvailableInLocalOrder(menu: Menu) {
      return menu.disponivelParaConsumoNoLocal;
    }

    function filterAvailableToDelivery(menu: Menu) {
      return menu.disponivelParaEntrega;
    }
  }, [isAInternalMenu, isOrderedOnRestaurant, isLoggedInClientArea, originalMenus]);

  return menus;
}

export function useFavoriteMenu(menus: Menu[]) {
  const [favoriteMenu, setFavoriteMenu] = useState<Menu | null>(null);

  const createFavoriteMenu = useCallback(async () => {
    if (menus.length === 0) {
      setFavoriteMenu(null);
      return;
    }

    const menuItemsIdsFavorited = FavoritedMenuItemsStorage.getAll().map((info) => info.id);
    const menuItemsFavorited = menus.reduce((previous, current) => {
      const favoritedItems = onlyFavoritedItems().map((item) =>
        MenuItemFactory.createMenuItemFavorited({
          menu: current,
          menuItem: item,
        })
      );
      return previous.concat(favoritedItems);

      function onlyFavoritedItems() {
        return current.itens.filter((item) => menuItemsIdsFavorited.includes(item.id));
      }
    }, [] as MenuItem[]);

    if (menuItemsFavorited.length > 0) setFavoriteMenu(createMenu());
    else setFavoriteMenu(null);

    function createMenu(): Menu {
      const date = new Date().toISOString();
      return {
        restauranteId: menus[0].restauranteId,
        disponibilidade: [],
        requestDate: date,
        dataCadastro: date,
        id: 'favorites',
        nome: 'Favoritos',
        tipoMenu: MenuTypes.Favorite,
        itens: menuItemsFavorited,
        ativo: true,
        disponivelParaEntrega: true,
        disponivelParaConsumoNoLocal: true,
        disponivel: true,
        ordem: 0,
        sempreDisponivel: true,
      };
    }
  }, [menus]);

  useEffect(() => {
    createFavoriteMenu();
  }, [createFavoriteMenu]);

  return { favoriteMenu, revalidateFavoriteMenu: createFavoriteMenu };
}
